import type { ImpactEffortPoint } from "@playbookai/widget-schemas";
import {
  impactEffortPillCenterXPx,
  impactEffortPillCenterYPx,
  impactEffortPillSize,
} from "./fan-out.js";

export interface ImpactEffortOverlapGroup {
  readonly id: string;
  readonly points: readonly ImpactEffortPoint[];
  readonly xPct: number;
  readonly yPct: number;
}

export interface ImpactEffortOverlapLayout {
  readonly groups: readonly ImpactEffortOverlapGroup[];
  readonly pairKeys: ReadonlySet<string>;
}

/**
 * Approximate the rendered pill boxes and build transitive overlap groups.
 * A previously grouped pair gets a small exit margin so tiny score/layout
 * changes do not make the group flash open and closed.
 */
export function groupImpactEffortOverlaps(
  points: readonly ImpactEffortPoint[],
  plot: { readonly width: number; readonly height: number },
  previousPairs: ReadonlySet<string> = new Set(),
  exitMarginPx = 10,
  positionFor: (point: ImpactEffortPoint) => {
    readonly xPct: number;
    readonly yPct: number;
  } = scorePosition,
  labelFor: (point: ImpactEffortPoint) => string = (point) => point.label,
): ImpactEffortOverlapLayout {
  const width = Math.max(1, plot.width);
  const height = Math.max(1, plot.height);
  const parent = points.map((_point, index) => index);
  const find = (index: number): number => {
    let current = index;
    while (parent[current] !== current) current = parent[current] ?? current;
    let cursor = index;
    while (parent[cursor] !== current) {
      const next = parent[cursor] ?? current;
      parent[cursor] = current;
      cursor = next;
    }
    return current;
  };
  const union = (left: number, right: number): void => {
    const leftRoot = find(left);
    const rightRoot = find(right);
    if (leftRoot !== rightRoot) parent[rightRoot] = leftRoot;
  };
  const boxes = points.map((point) => {
    const position = positionFor(point);
    const pill = impactEffortPillSize(labelFor(point));
    return {
      x: impactEffortPillCenterXPx(position.xPct, width, pill.width),
      y: impactEffortPillCenterYPx(position.yPct, height, pill.height),
      ...pill,
    };
  });
  for (let left = 0; left < points.length; left += 1) {
    for (let right = left + 1; right < points.length; right += 1) {
      const leftPoint = points[left];
      const rightPoint = points[right];
      const leftBox = boxes[left];
      const rightBox = boxes[right];
      if (!leftPoint || !rightPoint || !leftBox || !rightBox) continue;
      const pair = overlapPairKey(leftPoint.id, rightPoint.id);
      const margin = previousPairs.has(pair) ? exitMarginPx : 0;
      const touchesX =
        Math.abs(leftBox.x - rightBox.x) * 2 <=
        leftBox.width + rightBox.width + margin * 2;
      const touchesY =
        Math.abs(leftBox.y - rightBox.y) * 2 <=
        leftBox.height + rightBox.height + margin * 2;
      if (touchesX && touchesY) union(left, right);
    }
  }

  const grouped = new Map<number, ImpactEffortPoint[]>();
  points.forEach((point, index) => {
    const root = find(index);
    const members = grouped.get(root);
    if (members) members.push(point);
    else grouped.set(root, [point]);
  });
  const pairKeys = new Set<string>();
  const groups = [...grouped.values()].map((members) => {
    for (let left = 0; left < members.length; left += 1) {
      for (let right = left + 1; right < members.length; right += 1) {
        const leftPoint = members[left];
        const rightPoint = members[right];
        if (leftPoint && rightPoint) {
          pairKeys.add(overlapPairKey(leftPoint.id, rightPoint.id));
        }
      }
    }
    const positions = members.map(positionFor);
    return {
      id: members
        .map((point) => point.id)
        .sort()
        .join(":"),
      points: members,
      xPct:
        positions.reduce((sum, position) => sum + position.xPct, 0) /
        positions.length,
      yPct:
        positions.reduce((sum, position) => sum + position.yPct, 0) /
        positions.length,
    };
  });
  return { groups, pairKeys };
}

function overlapPairKey(left: string, right: string): string {
  return left < right ? `${left}:${right}` : `${right}:${left}`;
}

function scorePosition(point: ImpactEffortPoint): {
  xPct: number;
  yPct: number;
} {
  return {
    xPct: (10 - Math.max(0, Math.min(10, point.effort))) * 10,
    yPct: (10 - Math.max(0, Math.min(10, point.impact))) * 10,
  };
}
